import { useEffect, useMemo, useState } from "react";
import { useAdminCapability } from "../../auth/useAdminCapability";
import {
  buildDashboardBootstrapViewModel,
  dashboardBootstrapBackendAdapter,
  fetchDashboardBootstrapContractSnapshot,
  resolveDashboardBootstrapSnapshot,
} from "../api/dashboardBootstrapApi";
import type {
  DashboardBootstrapBackendAdapter,
  DashboardBootstrapSnapshot,
  DashboardBootstrapViewModel,
} from "../api/types/dashboardBootstrap.api";

interface DashboardBootstrapBackendState {
  principalKey: string | null;
  snapshot: DashboardBootstrapSnapshot | null;
  errorNotice: string | null;
}

function getSessionWorkspaceAccessMarker(user: unknown): string {
  if (!user || typeof user !== "object") {
    return "unknown-workspace-access";
  }

  const workspaceAccess = (user as { hasWorkspaceAccess?: unknown })
    .hasWorkspaceAccess;

  return String(workspaceAccess ?? "unknown-workspace-access");
}

function getBootstrapPrincipalKey(
  snapshot: DashboardBootstrapSnapshot,
): string | null {
  if (!snapshot.isAuthenticated) {
    return null;
  }

  return [
    snapshot.sessionUser?.id ?? "unknown-user",
    snapshot.sessionUser?.email ?? "unknown-email",
    snapshot.role ?? "unknown-role",
    snapshot.workspaceAccessState,
  ].join("|");
}

function getBootstrapFailureNotice(error: unknown): string {
  if (error instanceof Error && error.message) {
    return `Dashboard bootstrap is temporarily unavailable. ${error.message}`;
  }

  return "Dashboard bootstrap is temporarily unavailable. Failed to load dashboard bootstrap contract.";
}

export function useDashboardBootstrap(options?: {
  revalidateKey?: string | null;
  backendAdapter?: DashboardBootstrapBackendAdapter | null;
}): DashboardBootstrapViewModel {
  const capability = useAdminCapability({
    revalidateKey: options?.revalidateKey,
  });
  const [backendSnapshot, setBackendSnapshot] =
    useState<DashboardBootstrapBackendState>({
      principalKey: null,
      snapshot: null,
      errorNotice: null,
    });
  const sessionUser = capability.session?.user;
  const sessionIdentityKey = [
    sessionUser?.id ?? "unknown-user",
    sessionUser?.email ?? "unknown-email",
    String(sessionUser?.role ?? "unknown-role"),
    getSessionWorkspaceAccessMarker(sessionUser),
  ].join("|");

  const snapshot = useMemo(
    () =>
      resolveDashboardBootstrapSnapshot({
        isPending: capability.isCheckingAuth,
        data: capability.session ?? null,
      }),
    [capability.isCheckingAuth, capability.session, sessionIdentityKey],
  );
  const principalKey = useMemo(() => getBootstrapPrincipalKey(snapshot), [
    snapshot,
  ]);

  useEffect(() => {
    const adapter = options?.backendAdapter ?? dashboardBootstrapBackendAdapter;
    if (!adapter || !principalKey || snapshot.isLoading) {
      setBackendSnapshot({
        principalKey: null,
        snapshot: null,
        errorNotice: null,
      });
      return;
    }

    let isActive = true;

    setBackendSnapshot({
      principalKey,
      snapshot: null,
      errorNotice: null,
    });

    void fetchDashboardBootstrapContractSnapshot(adapter)
      .then((nextSnapshot) => {
        if (!isActive) {
          return;
        }

        setBackendSnapshot({
          principalKey,
          snapshot: nextSnapshot,
          errorNotice: null,
        });
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }

        setBackendSnapshot({
          principalKey,
          snapshot: null,
          errorNotice: getBootstrapFailureNotice(error),
        });
      });

    return () => {
      isActive = false;
    };
  }, [
    options?.backendAdapter,
    principalKey,
    snapshot.isLoading,
    options?.revalidateKey,
  ]);

  return useMemo(() => {
    // Ignore backend snapshots that were fetched for a different principal.
    const backendSnapshotForPrincipal =
      backendSnapshot.principalKey === principalKey
        ? backendSnapshot.snapshot
        : null;
    const backendFailureNotice =
      backendSnapshot.principalKey === principalKey
        ? backendSnapshot.errorNotice
        : null;
    const resolvedSnapshot = backendSnapshotForPrincipal ?? snapshot;
    const unauthorizedRoleNotice =
      resolvedSnapshot.isAuthenticated && !resolvedSnapshot.role
        ? "Your account does not have dashboard access yet."
        : null;
    const sessionNotice =
      capability.sessionNotice ??
      unauthorizedRoleNotice ??
      backendFailureNotice;

    return buildDashboardBootstrapViewModel(resolvedSnapshot, sessionNotice);
  }, [backendSnapshot, capability.sessionNotice, principalKey, snapshot]);
}
